import { useEffect, useRef, useState } from "react";
import { getCycleDetails, openOrCreateCycleEditDraft } from "../services/api";
import { useOptionalMultiWeekProgram } from "./MultiWeekProgramContext";

// Restores the multi-week draft of `cycleId` when the workout editor is opened
// directly (hard reload, deep link, restored tab) and the in-memory cycle is not
// the one in the URL. The editor must not evaluate its redirect guards until
// this settles.
//
// Safety: opening the draft is only attempted after a read-only check
// (getCycleDetails) says that this cycle already has a usable draft, so a reload
// can never create or recreate a draft. Without one -- or on any failure -- the
// editor reports `isUnavailable` and leaves for the read-only cycle view.
export function useCycleEditorRestore(cycleId) {
  const multiWeek = useOptionalMultiWeekProgram();
  const [failedCycleId, setFailedCycleId] = useState(null);
  const attemptedRef = useRef(null);
  // The provider value changes on every render, and StrictMode mounts effects
  // twice; neither may cancel the restore. Only leaving this cycle drops it.
  const multiWeekRef = useRef(multiWeek);
  multiWeekRef.current = multiWeek;
  const cycleIdRef = useRef(cycleId);
  cycleIdRef.current = cycleId;

  const metadata = multiWeek?.draftMetadata;
  const alreadyLoaded = Boolean(
    cycleId &&
      metadata?.loadedFromBackend &&
      metadata.cycleId === cycleId &&
      metadata.status === "draft"
  );
  const isUnavailable = Boolean(cycleId) && failedCycleId === cycleId;
  const needsRestore = Boolean(cycleId && multiWeek) && !alreadyLoaded && !isUnavailable;

  useEffect(() => {
    if (!needsRestore || attemptedRef.current === cycleId) {
      return undefined;
    }
    attemptedRef.current = cycleId;
    const isCurrent = () => cycleIdRef.current === cycleId;

    async function restore() {
      try {
        const details = await getCycleDetails(cycleId);
        if (!details?.draftPlanId || details?.draftState?.hasUsableDraft !== true) {
          if (isCurrent()) setFailedCycleId(cycleId);
          return;
        }
        if (!isCurrent()) return;
        await multiWeekRef.current.beginHydrationTarget({ cycleId, planId: null });
        // The precondition makes the backend refuse (409, no writes) instead of
        // creating a draft if this one vanished since the read-only check.
        const response = await openOrCreateCycleEditDraft(cycleId, {
          expectedDraftPlanId: details.draftPlanId,
        });
        if (!isCurrent()) return;
        multiWeekRef.current.hydrateProgramDraft(response);
        multiWeekRef.current.updateDraftMetadata({ cyclePlanId: response?.planId || null });
      } catch {
        if (isCurrent()) setFailedCycleId(cycleId);
      }
    }

    restore();
    return undefined;
  }, [cycleId, needsRestore]);

  return { isRestoring: needsRestore, isUnavailable };
}
